import { Injectable } from '@angular/core'
import { ProgressService } from '@mmx/components'
import { orderBy, sortBy } from 'lodash'
import { BehaviorSubject, Observable, of, Subject } from 'rxjs'
import {
  combineLatestWith,
  filter,
  finalize,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators'

import { AppointmentModel } from '../model/appointment'
import { AuthorizationModel } from '../model/authorization'
import { INSURANCE_PROVIDER_TRANSACTION_TYPE } from '../model/insurance-provider'
import { InsuranceProviderLookup } from '../model/insurance-provider-lookup'
import { Patient, PatientUploadedFile } from '../model/patient'
import { PatientInsurance } from '../model/patient-insurance'
import { ReferralModel } from '../model/referral'
import { TransactionModel } from '../model/transaction'
import { ParamsService } from '../services/params.service'
import { ApiService } from './api.service'
import { CrudService } from './crud.service'
import { InsuranceProviderLookupsService } from './insurance-provider-lookups.service'
import { PermissionsService } from './permissions.service'

const log = (message: string, meta?: any) => {
  // eslint-disable-next-line no-console
  console.log(`[PatientDataService]: ${message} ${meta ? JSON.stringify(meta) : ''}`)
}

export type PatientInsurancePair = [PatientInsurance, InsuranceProviderLookup]

export enum PATIENT_DATA_PROGRESS_KEY {
  APPOINTMENTS = 'appointments',
  AUTHORIZATIONS = 'authorizations',
  CARDS = 'cards',
  DOCUMENTS = 'documents',
  INSURANCES = 'insurances',
  PATIENT = 'patient',
  REFERRALS = 'referrals',
}

@Injectable({
  providedIn: 'root',
})
export class PatientDataService {
  transactions: TransactionModel[]
  photoIDs: PatientUploadedFile[]

  constructor(
    private restService: ApiService,
    private paramsService: ParamsService,
    private permissionsSvc: PermissionsService,
    private crudService: CrudService,
    private insuranceProviderLookupsService: InsuranceProviderLookupsService,
    public progressService: ProgressService<PATIENT_DATA_PROGRESS_KEY>,
  ) {}

  progress$ = this.progressService.progress$
  bustPatientCache$ = new BehaviorSubject<boolean>(false)
  bustAppointmentsCache$ = new BehaviorSubject<boolean>(false)

  patientId$: Observable<string> = this.paramsService.patientDefined$.pipe(
    tap(patientId => log(`patientId changed to ${patientId}`)),
    shareReplay(1),
  )

  patient$: Observable<Patient> = this.patientId$.pipe(
    filter(Boolean),
    tap(patientId => this.progressService.start(PATIENT_DATA_PROGRESS_KEY.PATIENT, { patientId })),
    switchMap((patientId) => this.restService.getPatient(patientId, true).pipe(
      finalize(() => {
        this.bustPatientCache$.next(false)
        this.progressService.stop(PATIENT_DATA_PROGRESS_KEY.PATIENT)
      }),
    )),
    tap(patient => log(`loaded patient ${patient.eid}`)),
    shareReplay(1),
  )

  insurancesChanged$ = new Subject<void>()

  insurances$: Observable<PatientInsurance[]> = this.patientId$.pipe(
    filter(Boolean),
    combineLatestWith(this.insurancesChanged$.pipe(startWith(null))),
    tap(([patientId]) => this.progressService.start(PATIENT_DATA_PROGRESS_KEY.INSURANCES, { patientId })),
    switchMap(([patientId]) => this.crudService.index(PatientInsurance, { patientId }).pipe(
      finalize(() => this.progressService.stop(PATIENT_DATA_PROGRESS_KEY.INSURANCES)),
    )),
    map(insurances => orderBy(insurances, insurance => insurance.typeIndex)),
    shareReplay(1),
  )

  insurancePairs$: Observable<PatientInsurancePair[]> = this.insurances$.pipe(
    combineLatestWith(this.insuranceProviderLookupsService.connected$),
    map(([insurances, lookups]) => {
      return insurances.map(insurance => {
        const lookup = lookups.find(({ id }) => id === insurance.carrierLookupId)
        return [insurance, lookup] as PatientInsurancePair
      })
    }),
    shareReplay(1),
  )

  isPriorAuthorizationPullAvailable$: Observable<boolean> = this.insurancePairs$.pipe(
    map(pairs => {
      const pair = pairs.find(([, lookup]) => {
        return this.insuranceProviderLookupsService.doesLookupSupport(lookup, INSURANCE_PROVIDER_TRANSACTION_TYPE.SERVICE_REVIEWS_INQUIRY)
      })
      return pair != null
    }),
  )

  appointments$: Observable<AppointmentModel[]> = this.bustAppointmentsCache$.pipe(
    combineLatestWith(this.patient$),
    tap(() => this.progressService.start(PATIENT_DATA_PROGRESS_KEY.APPOINTMENTS)),
    switchMap(([, patient]) => this.restService.getAppointments({
      patientId: patient.id,
      extend: 'products,facility',
    }).pipe(
      finalize(() => this.progressService.stop(PATIENT_DATA_PROGRESS_KEY.APPOINTMENTS)),
    )),
    map(response => sortBy(response.data, 'startAt')),
    shareReplay(1),
  )

  documents$ = this.patientId$.pipe(
    filter(() => this.permissionsSvc.instant('patient.document.view')),
    tap(() => this.progressService.start(PATIENT_DATA_PROGRESS_KEY.DOCUMENTS)),
    switchMap(patientId => this.restService.getPatientDocuments(patientId).pipe(
      finalize(() => this.progressService.stop(PATIENT_DATA_PROGRESS_KEY.DOCUMENTS)),
    )),
    shareReplay(1),
  )

  refreshCards$ = new Subject<void>()

  cards$ = this.patientId$.pipe(
    combineLatestWith(this.refreshCards$.pipe(startWith(null))),
    tap(() => this.progressService.start(PATIENT_DATA_PROGRESS_KEY.CARDS)),
    switchMap(([patientId]) => this.restService.getPatientCreditCards(patientId).pipe(
      finalize(() => this.progressService.stop(PATIENT_DATA_PROGRESS_KEY.CARDS)),
    )),
    shareReplay(1),
  )

  refreshReferrals$ = new Subject<void>()

  referrals$: Observable<ReferralModel[]> = this.patientId$.pipe(
    // combineLatestWith(this.clinic$),
    // filter(([, clinic]) => clinic.modules.referral),
    filter(() => this.permissionsSvc.instant('referral.view')),
    combineLatestWith(this.refreshReferrals$.pipe(startWith(null))),
    tap(() => this.progressService.start(PATIENT_DATA_PROGRESS_KEY.REFERRALS)),
    switchMap(([patientId]) => this.crudService.index(ReferralModel, { patientId }).pipe(
      finalize(() => this.progressService.stop(PATIENT_DATA_PROGRESS_KEY.REFERRALS)),
    )),
    shareReplay(1),
  )

  refreshAuthorizations$ = new Subject<void>()

  authorizations$: Observable<AuthorizationModel[]> = this.patientId$.pipe(
    // combineLatestWith(this.clinic$),
    // filter(([, clinic]) => clinic.modules.authorization),
    filter(() => this.permissionsSvc.instant('authorization.view')),
    combineLatestWith(this.refreshAuthorizations$.pipe(startWith(null))),
    tap(() => this.progressService.start(PATIENT_DATA_PROGRESS_KEY.AUTHORIZATIONS)),
    switchMap(([patientId]) => this.crudService.index(AuthorizationModel, { patientId }).pipe(
      finalize(() => this.progressService.stop(PATIENT_DATA_PROGRESS_KEY.AUTHORIZATIONS)),
    )),
    shareReplay(1),
  )

  getPatientUploadedFiles() {
    if (this.photoIDs) {
      return of(this.photoIDs)
    }

    return this.patient$.pipe(
      switchMap((patient) => this.restService.getPatientUploadedFiles(patient.id).pipe(
        map((res) => {
          this.photoIDs = res.data
          return this.photoIDs
        }),
      )),
    )
  }

  reset(): void {
    this.transactions = null
    this.photoIDs = null
  }
}
